import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  Circle, 
  User, 
  Briefcase, 
  CreditCard, 
  FileText, 
  Activity,
  ArrowRight
} from 'lucide-react';
import { Button } from '../../ui/button';
import { useAuth } from '../../../hooks/useAuth';
import { VendorService } from '../../../lib/services/vendorService';
import type { Database } from '../../../types/database';

type VendorOnboardingProgress = Database['public']['Tables']['vendor_onboarding_progress']['Row'];

interface OnboardingProgressProps {
  showInSidebar?: boolean;
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  required: boolean;
}

const ProgressBar = ({ value, className = '' }: { value: number; className?: string }) => (
  <div className={`relative h-2 w-full overflow-hidden rounded-full bg-gray-200 ${className}`}>
    <div
      className="h-full bg-blue-600 transition-all duration-300 ease-in-out"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

export default function OnboardingProgress({ showInSidebar = false }: OnboardingProgressProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState<VendorOnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProgressData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        setError(null);
        const progress = await VendorService.getOnboardingProgress(user.id);
        setProgressData(progress);
      } catch (err) {
        console.error('Error fetching onboarding progress:', err);
        setError(err instanceof Error ? err.message : 'Failed to load progress');
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [user?.id]);

  const getStepIcon = (stepId: string, completed: boolean) => {
    const iconClass = completed ? "w-4 h-4 text-green-600" : "w-4 h-4 text-gray-400";
    
    switch (stepId) {
      case 'basic_info':
        return <User className={iconClass} />;
      case 'service_types':
        return <Briefcase className={iconClass} />;
      case 'banking_details':
        return <CreditCard className={iconClass} />;
      case 'portfolio':
        return <FileText className={iconClass} />;
      default:
        return <Circle className={iconClass} />;
    }
  };

  // Calculate steps based on progress data
  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'basic_info',
      title: 'Basic Information',
      description: 'Name, email, phone, and city',
      completed: progressData?.basic_info_completed || false,
      required: true
    },
    {
      id: 'service_types',
      title: 'Service Types',
      description: 'Select the services you provide',
      completed: progressData?.services_completed || false,
      required: false
    },
    {
      id: 'banking_details',
      title: 'Banking Details',
      description: 'Payment and banking information',
      completed: progressData?.banking_completed || false,
      required: false
    },
    {
      id: 'portfolio',
      title: 'Portfolio',
      description: 'Upload your portfolio document',
      completed: progressData?.portfolio_completed || false,
      required: false
    }
  ];

  const completedSteps = onboardingSteps.filter(step => step.completed).length;
  const completionPercentage = progressData?.completion_percentage || 0;
  const isCompleted = completionPercentage === 100;

  if (loading) {
    return (
      <div className={`${showInSidebar ? 'p-4' : 'p-6'} animate-pulse`}>
        <div className="h-4 bg-gray-200 rounded mb-3"></div>
        <div className="h-2 bg-gray-200 rounded mb-4"></div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-3 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${showInSidebar ? 'p-4' : 'p-6'} text-center`}>
        <p className="text-sm text-red-600">Failed to load progress</p>
        <Button 
          onClick={() => window.location.reload()}
          size="sm"
          variant="outline"
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (showInSidebar) {
    return (
      <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-900">
            Profile Setup
          </span>
        </div>
        
        <div className="mb-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Progress</span>
            <span>{completionPercentage}%</span>
          </div>
          <ProgressBar value={completionPercentage} />
        </div>

        {!isCompleted && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-2">
              Complete your profile to unlock all features
            </p>
            <div className="space-y-1">
              {onboardingSteps.filter(step => !step.completed).slice(0, 2).map(step => (
                <div key={step.id} className="flex items-center gap-2 text-xs">
                  <Circle className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-600">{step.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Button 
          onClick={() => navigate('/vendor/profile')}
          size="sm"
          className="w-full bg-blue-600 text-white hover:bg-blue-700"
        >
          {isCompleted ? 'View Profile' : 'Complete Setup'}
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    );
  }

  // Full dashboard view
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Profile Setup</h2>
          <p className="text-sm text-gray-600">
            Complete your vendor profile to access all features
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{completionPercentage}%</div>
          <div className="text-sm text-gray-600">Complete</div>
        </div>
      </div>

      <div className="mb-6">
        <ProgressBar value={completionPercentage} className="h-3" />
      </div>

      <div className="space-y-4">
        {onboardingSteps.map((step) => (
          <div 
            key={step.id}
            className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-colors
              ${step.completed 
                ? 'bg-green-50 border-green-200' 
                : 'bg-gray-50 border-gray-200 hover:border-blue-300'
              }`}
          >
            <div className="flex-shrink-0">
              {step.completed ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                getStepIcon(step.id, false)
              )}
            </div>
            
            <div className="flex-grow">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">{step.title}</h3>
                {step.completed && (
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                    Complete
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{step.description}</p>
            </div>
            
            {!step.completed && (
              <Button
                onClick={() => navigate('/vendor/profile')}
                size="sm"
                variant="outline"
                className="flex-shrink-0"
              >
                Complete
              </Button>
            )}
          </div>
        ))}
      </div>

      {isCompleted && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-green-800">
              Congratulations! Your profile is complete.
            </p>
          </div>
          <p className="text-sm text-green-700 mt-1">
            You can now access all vendor features and receive task assignments.
          </p>
        </div>
      )}
    </div>
  );
} 